/**
 * Fixed, collapsible left sidebar.
 *
 * Entries are rendered from the navigation model, filtered by the keys the
 * SERVER said this caller may see. There is no role check in this file, and
 * there must never be one: the client renders a decision, it does not make it.
 *
 * The collapse control lives here, at the top of the panel it collapses,
 * rather than in the header: a control belongs next to the thing it acts on.
 */

import { NavLink } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';

import {
  NAV_GROUP_ORDER,
  entriesInGroup,
  type NavGroup,
} from '@/navigation/navigation';
import { useSessionScope } from '@/session/SessionScopeContext';

import { BrandMark } from './BrandMark';

/** The group headings, title-cased for reading rather than shouted in code. */
const GROUP_LABELS: Readonly<Record<string, string>> = {
  ORGANIZATION: 'Organization',
  COMMITMENT: 'Commitment',
  MANAGEMENT: 'Management',
  MONITOR: 'Monitor',
};

/**
 * Collapsed, the heading becomes a plain rule: seventy-two pixels cannot hold
 * "Organization", and the truncated stub that used to sit there read as a bug.
 * The words are hidden in CSS rather than dropped from the DOM, so the groups
 * still announce themselves to a screen reader at either width.
 */
function GroupLabel({ group }: { group: NavGroup }) {
  if (group === null) return null;
  return <div className="nav-group-label">{GROUP_LABELS[group] ?? group}</div>;
}

export function Sidebar({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  const { canSee } = useSessionScope();

  return (
    <aside className="atlas-sidebar" aria-label="Main navigation">
      <div className="atlas-brand">
        <span className="atlas-brand-mark">
          <BrandMark />
        </span>
        <span className="atlas-brand-text">
          <span className="atlas-wordmark">Atlas</span>
          <span className="atlas-brand-sub">GFT US</span>
        </span>
        <button
          type="button"
          className="icon-btn nav-collapse-btn"
          onClick={onToggle}
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        >
          {collapsed ? (
            <PanelLeftOpen aria-hidden="true" />
          ) : (
            <PanelLeftClose aria-hidden="true" />
          )}
        </button>
      </div>

      <nav className="atlas-nav">
        {NAV_GROUP_ORDER.map((group) => {
          const visible = entriesInGroup(group).filter((entry) => canSee(entry.key));
          // A group whose every entry is hidden shows no heading either.
          if (visible.length === 0) return null;

          return (
            <div key={group ?? 'ungrouped'}>
              <GroupLabel group={group} />
              {visible.map((entry) => {
                const Icon = entry.icon;
                return (
                  <NavLink
                    key={entry.key}
                    to={entry.path}
                    data-label={entry.label}
                    className={({ isActive }) =>
                      isActive ? 'nav-link is-active' : 'nav-link'
                    }
                  >
                    <span className="nav-icon">
                      <Icon aria-hidden="true" />
                    </span>
                    <span className="nav-label">{entry.label}</span>
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
